// Texto 12 C: My Planner Web — SÓ UM COMPUTADOR CONECTADO POR VEZ (regra fixa).
// O servidor guarda qual sessão do navegador é "o computador" da conta (tabela computador_conectado,
// supabase/migracao-0.74-computador.sql). O celular vê o cartão "Computador conectado" e pode desconectar;
// o navegador confere de tempos em tempos se ainda é o computador da conta e, se não for, sai sozinho.
// Sem a 0.74 no servidor, nada disso trava: o app segue como antes (vários computadores).
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { supabase } from './supabase';
import { DEMO } from './env';

export type Computador = { navegador: string; sistema: string; conectado_em: string };

/** Nome do navegador e do sistema deste computador (para o cartão no celular). */
export function infoNavegador(): { navegador: string; sistema: string } {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return { navegador: '', sistema: '' };
  const ua = navigator.userAgent;
  const navegador = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const sistema = /Windows/.test(ua) ? 'Windows' : /CrOS/.test(ua) ? 'ChromeOS' : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return { navegador, sistema };
}

const semServidor = (e: { code?: string; message?: string } | null) =>
  !!e && (e.code === 'PGRST202' || e.code === '42P01' || e.code === '42883' || /does not exist|Could not find/i.test(e.message ?? ''));

/** Computador conectado à conta (null = nenhum; undefined = o servidor ainda não tem a 0.74 ou sem internet). */
export async function computadorAtual(): Promise<Computador | null | undefined> {
  if (DEMO || !supabase) return null;
  const { data, error } = await supabase.from('computador_conectado').select('navegador, sistema, conectado_em').maybeSingle();
  if (error) return undefined;
  return (data as Computador | null) ?? null;
}

/** Desconecta o computador (pelo celular, ou o próprio computador ao sair). A sessão dele é encerrada no servidor. */
export async function desconectarComputador(): Promise<void> {
  if (DEMO || !supabase) return;
  const { error } = await supabase.rpc('computador_desconectar');
  if (error && !semServidor(error)) throw error;
}

/** O navegador acabou de entrar: vira o computador da conta. "outro" = já existe outro computador conectado. */
export async function registrarComputador(trocar: boolean): Promise<'ok' | 'outro'> {
  if (DEMO || !supabase || Platform.OS !== 'web') return 'ok';
  const { navegador, sistema } = infoNavegador();
  const { data, error } = await supabase.rpc('computador_registrar', { p_navegador: navegador, p_sistema: sistema, p_trocar: trocar });
  if (error) return 'ok';   // servidor sem a 0.74 ou sem internet: não impede de usar
  return data === 'outro' ? 'outro' : 'ok';
}

/** Este navegador ainda é o computador da conta? (false = foi desconectado pelo celular ou trocado por outro) */
export async function aindaConectado(): Promise<boolean> {
  if (DEMO || !supabase || Platform.OS !== 'web') return true;
  const { data, error } = await supabase.rpc('computador_conferir');
  if (error) return true;   // sem internet ou servidor sem a 0.74: na dúvida, não derruba
  return data !== false;
}

// ─── Bloqueio automático do computador (tempo parado) ───
const CHAVE_BLOQUEIO = 'mp_web_bloqueio_min';
export const OPCOES_BLOQUEIO = [5, 15, 30];
export async function tempoBloqueio(): Promise<number> {
  try { const v = Number(await AsyncStorage.getItem(CHAVE_BLOQUEIO)); return OPCOES_BLOQUEIO.includes(v) ? v : 15; } catch { return 15; }
}
export async function definirTempoBloqueio(min: number) {
  try { await AsyncStorage.setItem(CHAVE_BLOQUEIO, String(min)); } catch { /* fica o padrão */ }
  ouvintes.forEach(f => f(min));
}
const ouvintes = new Set<(min: number) => void>();
export const aoMudarBloqueio = (f: (min: number) => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };
